import { useEffect, useState, useCallback } from "react";
import { useApp } from "@/context/AppContext";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { PeriodFilter } from "@/components/PeriodFilter";
import { KpiCard } from "@/components/KpiCard";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { fmtDA, MODES_LABEL } from "@/lib/format";
import { downloadPdf } from "@/lib/files";
import { FileSpreadsheet, Printer, Loader2, Receipt, FileBarChart } from "lucide-react";
import { toast } from "sonner";

function ReportTable({ title, rows, isMode }) {
  const total = rows.reduce((s, r) => s + r.total, 0);
  return (
    <div className="rounded-xl border bg-card overflow-hidden">
      <div className="px-5 py-4 border-b"><h3 className="font-heading font-bold">{title}</h3></div>
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/50">
            <TableHead>Libellé</TableHead>
            <TableHead className="text-right">Montant</TableHead>
            <TableHead className="text-right w-24">%</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 && <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-6">Aucune donnée</TableCell></TableRow>}
          {rows.map((r) => (
            <TableRow key={r.label}>
              <TableCell className="text-sm">{isMode ? (MODES_LABEL[r.label] || r.label) : r.label}</TableCell>
              <TableCell className="tabular text-right font-medium">{fmtDA(r.total)}</TableCell>
              <TableCell className="tabular text-right text-muted-foreground">{total ? Math.round((r.total / total) * 100) : 0}%</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default function Rapports() {
  const { societeParam, societeId, societes, exercice } = useApp();
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [clients, setClients] = useState([]);
  const [clientFilter, setClientFilter] = useState("all");

  const sidQuery = () => (societeId !== "all" ? `societe_id=${societeId}&` : "");
  const genCA = () => downloadPdf(`/pdf/ca-annuel?${sidQuery()}`, "chiffre-affaires-2025-2026");
  const genEtatClients = () => {
    const p = new URLSearchParams();
    if (societeId !== "all") p.set("societe_id", societeId);
    if (exercice !== "all") p.set("annee", exercice);
    if (clientFilter !== "all") p.set("client_id", clientFilter);
    downloadPdf(`/pdf/etat-clients?${p.toString()}`, "etat-clients");
  };

  useEffect(() => {
    api.get("/clients", { params: societeParam }).then((r) => {
      const arr = Array.isArray(r.data) ? r.data : (r.data.items || r.data || []);
      setClients(arr); setClientFilter("all");
    }).catch(() => setClients([]));
  }, [societeId]); // eslint-disable-line

  const pdfSid = () => (societeId !== "all" ? societeId : (societes[0]?.id || ""));
  const genJournal = (type) => {
    const sid = pdfSid();
    if (!sid) return toast.error("Sélectionnez une société.");
    downloadPdf(`/pdf/journal-${type}?societe_id=${sid}&annee=${exercice}`, `journal-${type}-${exercice}`);
  };

  const load = useCallback(async () => {
    setData(null);
    try {
      const { data } = await api.get("/rapports/depenses", { params: { ...societeParam, period } });
      setData(data);
    } catch (e) { toast.error(apiError(e)); setData(false); }
  }, [societeId, period]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await api.get("/rapports/depenses/export", { params: { ...societeParam, period }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `rapport_depenses_${period}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export Excel téléchargé.");
    } catch (e) { toast.error(apiError(e)); }
    finally { setExporting(false); }
  };

  return (
    <div>
      <PageHeader
        title="Rapports"
        subtitle="État des dépenses par catégorie, bénéficiaire et mode de paiement"
        testid="rapports-page"
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => window.print()} className="gap-2" data-testid="print-btn"><Printer className="h-4 w-4" /> Imprimer</Button>
            <Button onClick={exportExcel} disabled={exporting} className="gap-2" data-testid="export-btn">
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />} Export Excel
            </Button>
          </div>
        }
      />

      <div className="mb-6"><PeriodFilter value={period} onChange={setPeriod} /></div>

      <div className="rounded-xl border bg-card p-5 mb-6" data-testid="etats-pdf-section">
        <h3 className="font-heading font-bold mb-1">États PDF par exercice <span className="text-sm font-normal text-muted-foreground">({exercice})</span></h3>
        <p className="text-sm text-muted-foreground mb-4">Journaux officiels à éditer pour la société sélectionnée.</p>
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" onClick={() => genJournal("ventes")} className="gap-2" data-testid="rapport-journal-ventes-btn"><FileBarChart className="h-4 w-4" /> Journal des ventes (PDF)</Button>
          <Button variant="outline" onClick={() => genJournal("achats")} className="gap-2" data-testid="rapport-journal-achats-btn"><FileBarChart className="h-4 w-4" /> Journal des achats (PDF)</Button>
        </div>
      </div>

      <div className="rounded-xl border bg-card p-5 mb-6" data-testid="etats-consolides-section">
        <h3 className="font-heading font-bold mb-1">États consolidés <span className="text-sm font-normal text-muted-foreground">(toutes sociétés si « Toutes » est sélectionné)</span></h3>
        <p className="text-sm text-muted-foreground mb-4">Chiffre d'affaires par produit (valeur &amp; quantité) 2025/2026, et état des clients.</p>
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="outline" onClick={genCA} className="gap-2" data-testid="rapport-ca-btn"><FileBarChart className="h-4 w-4" /> CA par produit 2025 &amp; 2026 (PDF)</Button>
          <div className="w-full sm:w-64">
            <Select value={clientFilter} onValueChange={setClientFilter}>
              <SelectTrigger data-testid="rapport-client-filter"><SelectValue placeholder="Client" /></SelectTrigger>
              <SelectContent className="bg-popover max-h-64">
                <SelectItem value="all">Tous les clients</SelectItem>
                {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.raison_sociale}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={genEtatClients} className="gap-2" data-testid="rapport-etat-clients-btn"><FileBarChart className="h-4 w-4" /> État des clients (PDF)</Button>
        </div>
      </div>

      {data === null ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : data === false ? (
        <p className="text-destructive">Erreur de chargement.</p>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 kpi-grid">
            <KpiCard testid="rap-total" label="Total des dépenses" value={data.total} icon={Receipt} tone="primary" />
            <KpiCard testid="rap-count" label="Nombre d'opérations" value={data.count} isCurrency={false} icon={Receipt} />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ReportTable title="Dépenses par catégorie" rows={data.par_categorie} />
            <ReportTable title="Dépenses par mode de paiement" rows={data.par_mode} isMode />
            <div className="lg:col-span-2"><ReportTable title="Dépenses par bénéficiaire" rows={data.par_beneficiaire} /></div>
          </div>
        </>
      )}
    </div>
  );
}
